import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import CategoryTabs from "../components/CategoryTabs";
import Toolbar from "../components/Toolbar";
import PlaceCard from "../components/PlaceCard";
import MapView from "../components/MapView";
import SubmitSpotDialog from "../components/SubmitSpotDialog";
import UpgradeDialog from "../components/UpgradeDialog";
import CreateListDialog from "../components/CreateListDialog";
import { categories } from "../mock";
import {
  fetchPlaces,
  fetchAllPlaces,
  fetchAreas,
  fetchLists,
  fetchListDetail,
} from "../api";
import { useAuth } from "../context/AuthContext";
import { Map as MapIcon, List as ListIcon, Loader2, Share2 } from "lucide-react";
import { Toaster, toast } from "sonner";

function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeCategory, setActiveCategory] = useState("all");
  const [activeListId, setActiveListId] = useState(null);
  const [activeList, setActiveList] = useState(null);
  const [area, setArea] = useState("all");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const [places, setPlaces] = useState([]);
  const [allPlaces, setAllPlaces] = useState([]);
  const [areas, setAreas] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);

  const [hoveredId, setHoveredId] = useState(null);
  const [mobileView, setMobileView] = useState("list");
  const [submitOpen, setSubmitOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'submit' | 'create'

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const loadMeta = useCallback(async () => {
    try {
      const [a, l, all] = await Promise.all([fetchAreas(), fetchLists(), fetchAllPlaces()]);
      setAreas(a);
      setLists(l);
      setAllPlaces(all);
    } catch (e) {
      console.error("meta load failed", e);
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        if (activeListId) {
          const detail = await fetchListDetail(activeListId);
          if (!cancelled) {
            setActiveList({ label: detail.label, author: detail.author, description: detail.description });
            setPlaces(detail.places);
          }
        } else {
          const data = await fetchPlaces({ category: activeCategory, area, q: debouncedQuery });
          if (!cancelled) {
            setActiveList(null);
            setPlaces(data);
          }
        }
      } catch (e) {
        console.error("places load failed", e);
        if (!cancelled) setPlaces([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [activeCategory, activeListId, area, debouncedQuery]);

  const counts = useMemo(() => {
    const c = {};
    for (const p of allPlaces) c[p.category] = (c[p.category] || 0) + 1;
    return c;
  }, [allPlaces]);

  const onSelectCategory = (id) => {
    setActiveListId(null);
    setActiveCategory(id);
    if (searchParams.get("list")) setSearchParams({}, { replace: true });
  };
  const onSelectList = (id) => {
    setActiveListId(id);
    setQuery("");
    setArea("all");
    setSearchParams({ list: id }, { replace: true });
  };
  const onQuery = (v) => {
    if (activeListId) {
      setActiveListId(null);
      setSearchParams({}, { replace: true });
    }
    setQuery(v);
  };
  const onArea = (v) => {
    if (activeListId) {
      setActiveListId(null);
      setSearchParams({}, { replace: true });
    }
    setArea(v);
  };

  // Deep-link: open a shared list from ?list=<id>
  useEffect(() => {
    const listParam = searchParams.get("list");
    if (listParam && listParam !== activeListId) {
      setActiveListId(listParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Premium-gated actions
  const requirePremium = (action, openDialog) => {
    if (!user) {
      toast("please log in first.");
      navigate("/login");
      return;
    }
    if (!user.is_premium) {
      setPendingAction(action);
      setUpgradeOpen(true);
      return;
    }
    openDialog();
  };

  const handleSubmitClick = () => requirePremium("submit", () => setSubmitOpen(true));
  const handleCreateListClick = () => requirePremium("create", () => setCreateOpen(true));

  const onUpgradeSuccess = () => {
    if (pendingAction === "create") setCreateOpen(true);
    else setSubmitOpen(true);
    setPendingAction(null);
  };

  const onListCreated = async (created) => {
    await loadMeta();
    if (created?.id) onSelectList(created.id);
  };

  const onCreated = async () => {
    await loadMeta();
    if (!activeListId) {
      const data = await fetchPlaces({ category: activeCategory, area, q: debouncedQuery });
      setPlaces(data);
    }
  };

  const grouped = useMemo(() => {
    if (activeListId) return [{ key: "list", header: null, items: places }];
    if (activeCategory !== "all") {
      const cat = categories.find((c) => c.id === activeCategory);
      return [{ key: cat?.id, cat, items: places }];
    }
    return categories
      .map((cat) => ({ key: cat.id, cat, items: places.filter((p) => p.category === cat.id) }))
      .filter((g) => g.items.length > 0);
  }, [activeListId, activeCategory, places]);

  const isEmpty = !loading && places.length === 0;

  return (
    <div className="flex h-screen flex-col bg-[#faf8f4] font-sans text-neutral-900">
      <Toaster position="top-center" richColors />
      <Header onGoPremium={() => setUpgradeOpen(true)} />
      <CategoryTabs
        activeCategory={activeCategory}
        activeListId={activeListId}
        counts={counts}
        total={allPlaces.length}
        lists={lists}
        onSelectCategory={onSelectCategory}
        onSelectList={onSelectList}
        onCreateList={handleCreateListClick}
      />
      <Toolbar
        query={query}
        onQuery={onQuery}
        area={area}
        onArea={onArea}
        areas={areas}
        onSubmitClick={handleSubmitClick}
      />

      <div className="relative flex flex-1 overflow-hidden">
        <div
          className={`${
            mobileView === "map" ? "hidden" : "block"
          } md:block w-full md:w-1/2 lg:w-[52%] overflow-y-auto px-4 md:px-6 py-5`}
        >
          {activeList && (
            <div className="mb-6 rounded-xl border border-neutral-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-baseline gap-2">
                    <h2 className="text-lg font-bold">{activeList.label}</h2>
                    <span className="text-sm font-medium text-neutral-500">by {activeList.author}</span>
                  </div>
                  <p className="mt-1 text-[14px] text-neutral-600">{activeList.description}</p>
                </div>
                <button
                  onClick={() => {
                    const url = `${window.location.origin}/?list=${activeListId}`;
                    navigator.clipboard?.writeText(url);
                    toast.success("link copied — share your list!");
                  }}
                  className="flex shrink-0 items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 hover:border-neutral-400"
                >
                  <Share2 className="h-3.5 w-3.5" /> Share
                </button>
              </div>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-20 text-neutral-400">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> loading spots…
            </div>
          )}

          {isEmpty && (
            <div className="py-20 text-center text-neutral-500">
              <p className="text-sm">no spots match that. try another search or neighbourhood.</p>
            </div>
          )}

          {!loading &&
            grouped.map(({ key, cat, items }) => (
              <section key={key} className="mb-8">
                {cat && (
                  <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <h2 className="text-lg font-bold text-neutral-900">{cat.label}</h2>
                    <p className="text-[13px] text-neutral-400">{cat.subtitle}</p>
                  </div>
                )}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {items.map((p) => (
                    <PlaceCard key={p.id} place={p} isActive={hoveredId === p.id} onHover={setHoveredId} />
                  ))}
                </div>
              </section>
            ))}

          {!loading && (
            <footer className="pb-10 pt-2 text-center text-xs text-neutral-400">
              made with love for delhi · a local's guide
              <span className="mt-1 block text-neutral-500">created by Jinendra Jain</span>
            </footer>
          )}
        </div>

        <div
          className={`${
            mobileView === "list" ? "hidden" : "block"
          } md:block absolute inset-0 md:relative md:inset-auto md:flex-1 border-l border-neutral-200`}
        >
          <MapView places={places} activeId={hoveredId} onHover={setHoveredId} />
        </div>

        <button
          onClick={() => setMobileView((v) => (v === "list" ? "map" : "list"))}
          className="md:hidden fixed bottom-5 left-1/2 z-[1000] flex -translate-x-1/2 items-center gap-2 rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-medium text-white shadow-lg"
        >
          {mobileView === "list" ? (
            <><MapIcon className="h-4 w-4" /> Map</>
          ) : (
            <><ListIcon className="h-4 w-4" /> List</>
          )}
        </button>
      </div>

      <SubmitSpotDialog open={submitOpen} onOpenChange={setSubmitOpen} onCreated={onCreated} />
      <CreateListDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        allPlaces={allPlaces}
        onCreated={onListCreated}
      />
      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        onSuccess={onUpgradeSuccess}
      />
    </div>
  );
}

export default Home;
